import { useMemo } from 'react'
import { Download } from 'lucide-react'

import {
  REPORT_COMPARE_OPTIONS,
  REPORT_PERIOD_PRESETS,
  resolveReportPeriodBounds,
} from '@/lib/reports/filters'
import { cn } from '@/lib/utils'

/**
 * @param {{
 *   period: string,
 *   compare: string,
 *   customFrom?: string,
 *   customTo?: string,
 *   onPeriodChange: (period: string) => void,
 *   onCompareChange: (compare: string) => void,
 *   onCustomFromChange?: (value: string) => void,
 *   onCustomToChange?: (value: string) => void,
 *   onExportCsv?: () => void,
 *   onExportPdf?: () => void,
 *   exporting?: boolean,
 *   children?: import('react').ReactNode,
 * }} props
 */
export function ReportFilterBar({
  period,
  compare,
  customFrom = '',
  customTo = '',
  onPeriodChange,
  onCompareChange,
  onCustomFromChange,
  onCustomToChange,
  onExportCsv,
  onExportPdf,
  exporting = false,
  children,
}) {
  const bounds = useMemo(() => {
    try {
      return resolveReportPeriodBounds(period, { from: customFrom, to: customTo })
    } catch {
      return null
    }
  }, [period, customFrom, customTo])

  return (
    <div className="flex flex-wrap items-center gap-2">
      {REPORT_PERIOD_PRESETS.map((option) => (
        <button
          key={option.id}
          type="button"
          onClick={() => onPeriodChange(option.id)}
          className={cn(
            'rounded-xl px-3 py-1.5 text-sm font-medium transition-colors',
            period === option.id
              ? 'bg-primary text-primary-foreground'
              : 'bg-muted/70 hover:bg-muted',
          )}
        >
          {option.label}
        </button>
      ))}

      {period === 'custom' ? (
        <>
          <input
            type="date"
            value={customFrom}
            onChange={(e) => onCustomFromChange?.(e.target.value)}
            className="h-9 rounded-xl border border-border/80 bg-card px-3 text-sm"
            aria-label="From date"
          />
          <input
            type="date"
            value={customTo}
            onChange={(e) => onCustomToChange?.(e.target.value)}
            className="h-9 rounded-xl border border-border/80 bg-card px-3 text-sm"
            aria-label="To date"
          />
        </>
      ) : bounds ? (
        <span className="text-xs text-muted-foreground">
          {bounds.from} → {bounds.to}
        </span>
      ) : null}

      <select
        value={compare}
        onChange={(e) => onCompareChange(e.target.value)}
        className="h-9 rounded-xl border border-border/80 bg-card px-3 text-sm"
        aria-label="Compare period"
      >
        {REPORT_COMPARE_OPTIONS.map((option) => (
          <option key={option.id} value={option.id}>
            {option.label}
          </option>
        ))}
      </select>

      {children}

      {onExportCsv ? (
        <button
          type="button"
          disabled={exporting}
          onClick={onExportCsv}
          className="inline-flex h-9 items-center gap-1.5 rounded-xl border border-border/80 bg-card px-3 text-sm font-medium hover:bg-muted/60 disabled:opacity-60"
        >
          <Download className="h-4 w-4" aria-hidden />
          CSV
        </button>
      ) : null}

      {onExportPdf ? (
        <button
          type="button"
          disabled={exporting}
          onClick={onExportPdf}
          className="inline-flex h-9 items-center gap-1.5 rounded-xl border border-border/80 bg-card px-3 text-sm font-medium hover:bg-muted/60 disabled:opacity-60"
        >
          <Download className="h-4 w-4" aria-hidden />
          PDF
        </button>
      ) : null}
    </div>
  )
}
